// Standard form field wrapper: label, hint, error, optional asterisk.
// Inputs go inside as children so each form can use any HTML element
// + spread `register('foo')` directly.

import type { ReactNode } from 'react';
import type { FieldError } from 'react-hook-form';

interface FieldProps {
  label: string;
  required?: boolean;
  hint?: string;
  error?: FieldError | { message?: string } | undefined;
  children: ReactNode;
  className?: string;
  /**
   * children 是**一組**控件 (radio / checkbox 群組、檔案拖放區) 而不是單一輸入框時傳 true。
   * 這種 children 自己就帶 <label>, 用 <label> 包會變成巢狀 label (無效 HTML);
   * 而且「一個 label 對一個控件」的模型本來就不適用 —— 正解是 fieldset/legend。
   */
  group?: boolean;
}

// 2026-07-20: label 原本跟 {children} 是**兄弟節點**且沒有 htmlFor —— 看起來對,
// 在無障礙樹裡整批欄位都是無名的 (讀屏軟體念不出欄位名, 點文字也不會聚焦,
// 密碼管理器抓不到)。 這支被 44 個檔案用 → 全站表單都中。
//
// 修法是把 children 包進 <label> 走**隱式關聯**, 而不是 useId+htmlFor:
// 後者要每個 caller 自己把 id 綁到控件上 (44 個檔案 × 每個欄位)。
// 外框維持 <div> 不動 —— 有些 Field 的 children 含按鈕 (ReferenceSelect /
// PartySearchInput), 整個換成 <label> 會讓按鈕點擊被 label 行為吃掉。
// 視覺不變: 原本 label→input 的間距來自外層 space-y-1, 現在改由 span 的 mb-1 給。
export function Field({ label, required, hint, error, children, className, group }: FieldProps) {
  const labelText = (
    <>
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </>
  );
  const labelCls = 'block text-sm font-medium text-slate-700 mb-1';
  return (
    <div className={className ?? 'space-y-1'}>
      {group ? (
        // fieldset 預設有 min-width:auto, 在 flex/grid 裡會撐破版面 → min-w-0。
        <fieldset className="border-0 p-0 m-0 min-w-0">
          <legend className={labelCls}>{labelText}</legend>
          {children}
        </fieldset>
      ) : (
        // biome-ignore lint/a11y/noLabelWithoutControl: 控件由 caller 以 children 傳入, 靜態分析看不到 — 隱式關聯正是這個 wrapper 的用途
        <label className="block">
          <span className={labelCls}>{labelText}</span>
          {children}
        </label>
      )}
      {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
      {error?.message && <p className="text-xs text-red-600">{String(error.message)}</p>}
    </div>
  );
}

// 2026-06-21: 加 disabled: variants — locked field (Edit LP / 法人名稱 / 國籍
// 等鎖住的欄位) 要明顯灰掉, 否則看起來像可以打字. Tailwind disabled:
// variant 對 input + select + textarea 一視同仁.
const DISABLED_VARIANT =
  'disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-200 disabled:cursor-not-allowed disabled:shadow-none';

export const inputClass = `w-full px-3 py-1.5 text-sm border border-slate-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${DISABLED_VARIANT}`;

// 行內/緊湊佈局用 (無 w-full) — 給 flex-row 表單列 (薪資/代理/保險等) 共用,
// 取代各檔手刻的 local inputCls (少了 focus ring / disabled variant; UX 稽核 #5)。
export const inputClassCompact = `px-3 py-1.5 text-sm border border-slate-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${DISABLED_VARIANT}`;

export const inputErrorClass = `w-full px-3 py-1.5 text-sm border border-red-400 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 ${DISABLED_VARIANT}`;

export function inputCls(hasError: boolean): string {
  return hasError ? inputErrorClass : inputClass;
}
